import { ref, computed, readonly } from 'vue';
import { getI18nInstance } from '@/i18n';
import { isAimetaBrowser } from '@/utils/platform';

/** default：知识库等；viewport90：直播间设置占视口 90% */
export type BrowserPageOverlaySize = 'default' | 'viewport90';

export type BrowserPageOverlayItem = {
  id: string;
  url: string;
  title: string;
  size: BrowserPageOverlaySize;
};

const OVERLAY_MSG_OPEN = 'aimeta:browser-overlay-open';
const OVERLAY_MSG_CLOSE = 'aimeta:browser-overlay-close';

let overlayIdSeq = 0;
const stack = ref<BrowserPageOverlayItem[]>([]);

function resolveTitle(options: { title?: string; titleKey?: string }): string {
  if (options.title) return options.title;
  if (options.titleKey) {
    const i18n = getI18nInstance();
    if (i18n?.global?.t) {
      return String(i18n.global.t(options.titleKey));
    }
    return options.titleKey;
  }
  return '';
}

/** 当前页是否在父窗口的浏览器弹出层 iframe 内 */
export function isInBrowserOverlayIframe(): boolean {
  if (!isAimetaBrowser()) return false;
  try {
    return window.self !== window.top;
  } catch {
    return false;
  }
}

function pushOverlayItem(options: {
  url: string;
  title?: string;
  titleKey?: string;
  size?: BrowserPageOverlaySize;
}): string {
  const id = `browser-overlay-${++overlayIdSeq}`;
  stack.value = [
    ...stack.value,
    {
      id,
      url: options.url,
      title: resolveTitle(options),
      size: options.size ?? 'default',
    },
  ];
  return id;
}

function postOpenToParent(options: {
  url: string;
  title?: string;
  titleKey?: string;
  size?: BrowserPageOverlaySize;
}) {
  window.parent.postMessage(
    { type: OVERLAY_MSG_OPEN, payload: options },
    window.location.origin
  );
}

function postCloseToParent(id?: string) {
  window.parent.postMessage(
    { type: OVERLAY_MSG_CLOSE, id },
    window.location.origin
  );
}

/**
 * 浏览器内打开弹出层（可多层叠加）；iframe 内调用会通知父窗口入栈，不替换已有层。
 */
export function openBrowserPageOverlay(options: {
  url: string;
  title?: string;
  titleKey?: string;
  size?: BrowserPageOverlaySize;
}): string {
  if (!isAimetaBrowser()) return '';
  if (isInBrowserOverlayIframe()) {
    postOpenToParent(options);
    return '';
  }
  return pushOverlayItem(options);
}

/** 仅关闭指定层或最顶层；须通过关闭按钮触发，遮罩不关闭 */
export function closeBrowserPageOverlay(id?: string): void {
  if (!isAimetaBrowser()) return;
  if (isInBrowserOverlayIframe()) {
    postCloseToParent(id);
    return;
  }
  if (!stack.value.length) return;
  if (id) {
    stack.value = stack.value.filter((item) => item.id !== id);
  } else {
    stack.value = stack.value.slice(0, -1);
  }
}

/** 父窗口监听 iframe 内发起的打开/关闭（仅安装一次） */
export function installBrowserPageOverlayBridge(): void {
  if (!isAimetaBrowser()) return;
  const win = window as Window & { __AIMETA_OVERLAY_BRIDGE__?: boolean };
  if (win.__AIMETA_OVERLAY_BRIDGE__) return;
  win.__AIMETA_OVERLAY_BRIDGE__ = true;

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as {
      type?: string;
      payload?: {
        url: string;
        title?: string;
        titleKey?: string;
        size?: BrowserPageOverlaySize;
      };
      id?: string;
    };
    if (!data?.type) return;
    if (data.type === OVERLAY_MSG_OPEN && data.payload?.url) {
      pushOverlayItem(data.payload);
    } else if (data.type === OVERLAY_MSG_CLOSE) {
      closeBrowserPageOverlay(data.id);
    }
  });
}

export function useBrowserPageOverlay() {
  return {
    stack: readonly(stack),
    overlayStack: computed(() => stack.value),
    hasOverlay: computed(() => stack.value.length > 0),
    close: closeBrowserPageOverlay,
  };
}
